import { TFunction } from 'i18next';
import { IOptionList, ISurveyResultsFilter } from 'models';
import { StatisticGroupingTypes } from 'modules/admin-panel/models';
import {
	IServiceStatItem,
	IStatisticItem,
	IStatisticParams,
	ITableService,
} from 'modules/admin-panel/pages/results/models';
import { convertCamelCaseIntoSnakeCase } from 'helpers';

export interface ITableRow
	extends Record<
		string,
		ITableService<number | string | null> | ITableRow[] | undefined
	> {
	name: ITableService<string>;
	responses: ITableService<number>;
	nps: ITableService<number | null>;
	statistics: ITableRow[] | undefined;
}

export const mapTableStatistic = (
	statistic: IStatisticItem[] | undefined,
): ITableRow[] | undefined =>
	statistic?.map(
		({ name, serviceStatistics, submittedCount, npsPercent, statistics }) => {
			const servicesStatisticData = serviceStatistics.reduce((res, i) => {
				return {
					...res,
					[i.name]: {
						value: i.satisfaction !== null ? Math.round(i.satisfaction) : null,
						trend: i.trend,
						previousVal:
							i.previousSatisfaction !== null
								? Math.round(i.previousSatisfaction)
								: null,
						firstVal:
							i.firstSatisfaction !== null
								? Math.round(i.firstSatisfaction)
								: null,
						serviceId: i.id,
					} as ITableService<number>,
				};
			}, {});

			return {
				name: { value: name },
				responses: { value: Math.round(submittedCount) },
				nps: {
					value: npsPercent !== undefined ? Math.round(npsPercent) : null,
				},
				...servicesStatisticData,
				statistics: statistics.length
					? mapTableStatistic(statistics)
					: undefined,
			};
		},
	);

export const mapStatisticParams = (
	filters: ISurveyResultsFilter,
	type: StatisticGroupingTypes,
): IStatisticParams => ({
	...filters,
	serviceId: undefined,
	roundId: undefined,
	surveyRoundId: filters.roundId,
	office: type === StatisticGroupingTypes.location ? undefined : filters.office,
	groupingType: type,
});

export const getStatisticTypes = (
	t: TFunction,
	withBusinessUnits: boolean,
): IOptionList<string> =>
	Object.keys(StatisticGroupingTypes)
		.filter(
			(key) =>
				!(key === StatisticGroupingTypes.businessUnit && !withBusinessUnits),
		)
		.map((key) => ({
			title: t(`titles.${convertCamelCaseIntoSnakeCase(key)}`),
			value: key,
		}));

const sortByResponses = (data?: ITableRow[]): ITableRow[] => {
	if (!data) return [];

	return data.sort((a, b) => {
		if (!a || !b || !a.responses || !b.responses) return 0;
		const valueA = a.responses.value;
		const valueB = b.responses.value;
		return valueB - valueA;
	});
};

const GLOBAL_STATISTIC_NAME = 'All';
const raiseStatisticOf = (locations: ITableRow[]): ITableRow[] =>
	locations.reduce((resultArray, location) => {
		if (location.name.value === GLOBAL_STATISTIC_NAME) {
			return resultArray.concat(location);
		}
		return resultArray.concat(location.statistics || []);
	}, [] as ITableRow[]);

const removeNesting = (locations: ITableRow[]): ITableRow[] =>
	locations.map((location) => ({
		...location,
		statistics: undefined,
	}));

const getLocationCities = (locationData?: ITableRow[]): ITableRow[] => {
	if (!locationData) return [];
	const countryLocations = raiseStatisticOf(locationData);
	return removeNesting(raiseStatisticOf(countryLocations));
};

export const prepareForSummary = (
	statistic: IStatisticItem[],
	raiseLocations?: boolean,
) => {
	const data = raiseLocations
		? getLocationCities(mapTableStatistic(statistic))
		: mapTableStatistic(statistic);
	const sortedData = sortByResponses(data);
	if (sortedData && sortedData.length > 14) {
		sortedData.length = 14;
	}
	return sortedData;
};

export const noPrepareForSummary = (statistic: IStatisticItem[]) => {
	return statistic;
};
